import { useCallback, useEffect, useState } from 'react';

import AmountInput from './AmountInput/AmountInput';
import ProviderSelect from './ProviderSelect/ProviderSelect';
import { AccountEventOptions } from '../utils/eventOptions';
import { Button, FormHelperText } from '@fhl-world/salmon-ui';
import { AmountSuggestParams } from './AmountInput/utils/suggestTypes';
import { ErrorResponse } from '../../../types/generated/pos';
import styled from '@emotion/styled';
import { useGetProviders } from '@api/services/providers/getProviders';

const WithMargin = styled.div`
	margin: 16px 0 24px;
`;

export type PaymentFormValues = {
	amount: number;
	providerCode: string;
};

type PaymentFormProps = {
	initialValues: Partial<PaymentFormValues>;
	onSubmit: (values: PaymentFormValues) => void;
	eventOptions: AccountEventOptions;
	requiredPayment?: number;
	outstandingBalance?: number;
	amountSuggest?: AmountSuggestParams;
	loading?: boolean;
	serverError: ErrorResponse | null | string;
};

const PaymentForm = (props: PaymentFormProps) => {
	const {
		initialValues,
		onSubmit,
		eventOptions,
		amountSuggest,
		loading,
		serverError,
	} = props;

	const { data: providerListResponse } = useGetProviders();

	const [providerCode, setProviderCode] = useState(initialValues?.providerCode);
	const [amount, setAmount] = useState(initialValues?.amount);

	const [validation, setValidation] = useState({
		amount: '',
		payment: '',
		submit: '',
	});

	const handleSubmit = useCallback(() => {
		if (amount === undefined || !providerCode) {
			return;
		}

		onSubmit({ providerCode, amount });
	}, [amount, providerCode, onSubmit]);

	useEffect(() => {
		if (!serverError) {
			return;
		}

		const errorText =
			typeof serverError === 'string' ? serverError : serverError.message;

		setValidation({ submit: errorText, amount: '', payment: '' });
	}, [serverError]);

	return (
		<div>
			<AmountInput
				validation={validation}
				value={amount}
				eventOptions={eventOptions}
				amountSuggest={amountSuggest}
				onChange={(inputAmount) => {
					setValidation({ ...validation, amount: '' });
					setAmount(inputAmount);
				}}
				disabled={loading}
			/>

			<WithMargin>
				<ProviderSelect
					providerListResponse={providerListResponse}
					eventOptions={eventOptions}
					providerCode={providerCode}
					validation={validation}
					onChange={(inputProviderCode) => {
						setValidation({ ...validation, payment: '' });
						setProviderCode(inputProviderCode);
					}}
					disabled={loading}
				/>
			</WithMargin>

			<Button
				onClick={handleSubmit}
				variant="contained"
				fullWidth
				disabled={loading}
				size="large"
			>
				Proceed to pay
			</Button>
			{!!validation.submit && (
				<FormHelperText error>{validation.submit}</FormHelperText>
			)}
		</div>
	);
};

export default PaymentForm;
